import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit3, FiUser, FiBriefcase, FiTrash2 } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import ConfirmModal from '../../components/UI/Modal/ConfirmModal';
import { clientesService, vendedoresService } from '../../services';

const ClienteDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [vendedor, setVendedor] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Opciones para los selectores (solo para mostrar labels)
  const categorias = [
    { value: 'DISTRIBUTOR', label: 'DISTRIBUIDOR' },
    { value: 'TIENDA', label: 'TIENDA' }
  ];

  const tiposCliente = [
    { value: 'BETEL_NATURAL', label: 'BETEL NATURAL' },
    { value: 'NATURALANDLAB', label: 'NATURALANDLAB' },
    { value: 'AMBOS', label: 'AMBOS' }
  ];

  const tiposPago = [
    { value: 'COD', label: 'COD (Pago antes de enviar la orden)' },
    { value: 'TERMINOS', label: 'TÉRMINOS' }
  ];

  const tiposEnvio = [
    { value: 'UPS', label: 'UPS' },
    { value: 'PALLET', label: 'PALETA' },
    { value: 'DRIVER_DELIVERY', label: 'ENTREGA DEL CHOFER' },
    { value: 'PICKUP_BETEL', label: 'RECOGER EN BETEL' }
  ];

  const loadClienteDetalle = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const result = await clientesService.getCliente(id);

      if (result.success) {
        // Transformar los datos de la API al formato esperado por el componente
        const clienteData = {
          id: result.data.id,
          // Información básica
          clientType: result.data.client_type || 'individual',
          companyName: result.data.company_name || '',
          taxId: result.data.tax_id || '',
          fullName: result.data.full_name || '',
          phone: result.data.phone || '',
          mobile: result.data.mobile || '',
          phones: result.data.phones || result.data.phone_list || (result.data.phone ? [{
            number: result.data.phone,
            type: 'office',
            is_primary: true
          }] : []),
          email: result.data.email || '',

          // Dirección de negocio
          businessAddress: result.data.business_address || '',
          businessUnit: result.data.business_unit || '',
          businessCity: result.data.business_city || '',
          businessState: result.data.business_state || '',
          businessPostalCode: result.data.business_postal_code || '',
          businessCountry: result.data.business_country || 'Estados Unidos',

          // Dirección de entrega
          sameDeliveryAddress: result.data.same_delivery_address !== false,
          deliveryAddress: result.data.delivery_address || '',
          deliveryUnit: result.data.delivery_unit || '',
          deliveryCity: result.data.delivery_city || '',
          deliveryState: result.data.delivery_state || '',
          deliveryPostalCode: result.data.delivery_postal_code || '',
          deliveryCountry: result.data.delivery_country || '',
          isResidential: result.data.is_residential || false,

          // Tipo de envío
          shippingType: result.data.shipping_type || '',

          // Impuestos y pagos
          requiresTaxes: result.data.requires_taxes || false,
          paymentType: result.data.payment_type || 'TERMINOS',
          creditLimit: result.data.credit_limit || 0,
          paymentTerms: result.data.payment_terms || 0,

          // Categorización
          businessType: result.data.business_type || '',
          clientCategory: result.data.client_category || '',
          assignedUserId: result.data.assigned_user_id || null,

          // Estado
          status: result.data.is_active ? 'activo' : 'inactivo'
        };

        setCliente(clienteData);

        // Cargar información del vendedor si está asignado
        if (clienteData.assignedUserId) {
          try {
            const vendedorResult = await vendedoresService.getVendedor(clienteData.assignedUserId);
            if (vendedorResult.success) {
              setVendedor(vendedorResult.data);
            }
          } catch (error) {
            console.error('Error al cargar vendedor:', error);
          }
        }
      } else {
        console.error('Error al cargar cliente:', result.message);
        setCliente(null);
      }
    } catch (error) {
      console.error('Error cargando cliente:', error);
      setCliente(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadClienteDetalle();
  }, [loadClienteDetalle]);

  const getLabel = (options, value) => {
    const option = options.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const result = await clientesService.deleteCliente(id);
      if (result.success) {
        navigate('/system/clientes', {
          state: {
            message: `Cliente "${cliente?.companyName || cliente?.fullName || ''}" desactivado correctamente`,
            type: 'success'
          }
        });
      } else {
        console.error('Error al desactivar cliente:', result.message);
      }
    } catch (error) {
      console.error('Error desactivando cliente:', error);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (isLoading) {
    return <LoadingGrid count={4} message="Cargando información del cliente..." />;
  }

  if (!cliente) {
    return (
      <div className="error-container">
        <h2>Cliente no encontrado</h2>
        <button onClick={() => navigate('/system/clientes')} className="btn btn--primary">
          Volver a Clientes
        </button>
      </div>
    );
  }

  return (
    <div className="cliente-detalle-page">
      {/* Header */}
      <div className="form-header">
        <button
          className="btn btn--ghost"
          onClick={() => navigate('/system/clientes')}
          type="button"
        >
          <FiArrowLeft /> Volver a Clientes
        </button>

        <h1 className="form-title">
          DATOS DEL CLIENTE
        </h1>

        <div className="form-actions">
          <button
            className="btn btn--outline"
            onClick={() => setShowDeleteModal(true)}
            type="button"
            disabled={isDeleting}
          >
            <FiTrash2 /> Eliminar
          </button>
          <button
            className="btn btn--primary"
            onClick={() => navigate(`/system/clientes/${id}/editar`)}
            type="button"
          >
            <FiEdit3 /> Editar Cliente
          </button>
        </div>
      </div>

      {/* Formulario de visualización */}
      <div className="cliente-detail-form">
        <div className="form-columns">
          {/* Columna Izquierda */}
          <div className="form-column">
            {/* Sección: Información Básica */}
            <div className="form-section">
              <div className="section-header">
                <FiBriefcase className="section-icon" />
                <h2 className="section-title">Información Básica</h2>
              </div>

              <div className="form-group">
                <label className="form-label">Nombre de Negocio</label>
                <div className="form-value">{cliente.companyName}</div>
              </div>
            </div>

            {/* Sección: Dirección del Negocio */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Dirección del Negocio</h3>
              </div>

              <div className="form-group">
                <label className="form-label">Número y Calle</label>
                <div className="form-value">{cliente.businessAddress}</div>
              </div>

              {cliente.businessUnit && (
                <div className="form-group">
                  <label className="form-label">Unidad</label>
                  <div className="form-value">{cliente.businessUnit}</div>
                </div>
              )}

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Ciudad</label>
                  <div className="form-value">{cliente.businessCity}</div>
                </div>

                <div className="form-group">
                  <label className="form-label">Estado</label>
                  <div className="form-value">{cliente.businessState}</div>
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Código Postal</label>
                  <div className="form-value">{cliente.businessPostalCode}</div>
                </div>

                <div className="form-group">
                  <label className="form-label">País</label>
                  <div className="form-value">{cliente.businessCountry}</div>
                </div>
              </div>
            </div>

            {/* Sección: Dirección de Entrega */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Dirección de Entrega</h3>
              </div>

              <div className="form-group">
                <div className="form-value">
                  {cliente.sameDeliveryAddress ? 'MISMA DIRECCIÓN' : 'OTRA DIRECCIÓN'}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Tipo de dirección</label>
                <div className="form-value">
                  {cliente.isResidential ? 'RESIDENCIAL' : 'COMERCIAL'}
                </div>
              </div>

              {!cliente.sameDeliveryAddress && (
                <>
                  <div className="form-group">
                    <label className="form-label">Número y Calle</label>
                    <div className="form-value">{cliente.deliveryAddress}</div>
                  </div>

                  {cliente.deliveryUnit && (
                    <div className="form-group">
                      <label className="form-label">Unidad</label>
                      <div className="form-value">{cliente.deliveryUnit}</div>
                    </div>
                  )}

                  <div className="form-grid-2">
                    <div className="form-group">
                      <label className="form-label">Ciudad</label>
                      <div className="form-value">{cliente.deliveryCity}</div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Estado</label>
                      <div className="form-value">{cliente.deliveryState}</div>
                    </div>
                  </div>

                  <div className="form-grid-2">
                    <div className="form-group">
                      <label className="form-label">Código Postal</label>
                      <div className="form-value">{cliente.deliveryPostalCode}</div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">País</label>
                      <div className="form-value">{cliente.deliveryCountry}</div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Sección: Tipos de Envíos */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Tipos de Envíos</h3>
              </div>

              <div className="form-group">
                <div className="form-value">
                  {getLabel(tiposEnvio, cliente.shippingType)}
                </div>
              </div>
            </div>
          </div>

          {/* Columna Derecha */}
          <div className="form-column">
            {/* Sección: Información de Contacto */}
            <div className="form-section">
              <div className="section-header">
                <FiUser className="section-icon" />
                <h2 className="section-title">Contacto</h2>
              </div>

              <div className="form-group">
                <label className="form-label">Nombre Completo</label>
                <div className="form-value">{cliente.fullName}</div>
              </div>

              <div className="form-group">
                <label className="form-label">Teléfonos</label>
                {cliente.phones && cliente.phones.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {cliente.phones.map((phone, index) => (
                      <div key={index} className="form-value" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px',
                        backgroundColor: phone.is_primary ? '#f0f9ff' : '#f9fafb',
                        borderRadius: '4px',
                        border: phone.is_primary ? '1px solid #7B2D8E' : '1px solid #e5e7eb'
                      }}>
                        <span style={{
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#666',
                          textTransform: 'uppercase',
                          minWidth: '60px'
                        }}>
                          {phone.type === 'mobile' ? 'Móvil' :
                           phone.type === 'office' ? 'Oficina' :
                           phone.type === 'home' ? 'Casa' :
                           phone.type === 'fax' ? 'Fax' : 'Otro'}:
                        </span>
                        <span style={{ fontWeight: phone.is_primary ? '600' : '400' }}>
                          {phone.number}
                        </span>
                        {phone.is_primary && (
                          <span style={{
                            fontSize: '0.7rem',
                            backgroundColor: '#7B2D8E',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            marginLeft: 'auto'
                          }}>
                            PRINCIPAL
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="form-value">{cliente.phone || 'Sin teléfono'}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <div className="form-value">{cliente.email}</div>
              </div>
            </div>

            {/* Sección: Impuestos - OCULTA */}
            <div className="form-section" style={{ display: 'none' }}>
              <div className="section-header">
                <h3 className="section-title">Impuestos</h3>
              </div>

              <div className="form-group">
                <div className="form-value">
                  {cliente.requiresTaxes ? 'SI' : 'NO'}
                </div>
              </div>
            </div>

            {/* Sección: Tipos de Pago */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Tipos de Pago</h3>
              </div>

              <div className="form-group">
                <div className="form-value">
                  {getLabel(tiposPago, cliente.paymentType)}
                </div>
              </div>

              {cliente.paymentType === 'TERMINOS' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Límite de Crédito</label>
                    <div className="form-value">${Number(cliente.creditLimit || 0) % 1 === 0 ? Number(cliente.creditLimit || 0).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : Number(cliente.creditLimit || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Días de Pago</label>
                    <div className="form-value">{cliente.paymentTerms} días</div>
                  </div>
                </>
              )}
            </div>

            {/* Sección: Tipo de Cliente */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Tipo de Cliente</h3>
              </div>

              <div className="form-group">
                <label className="form-label">Tipo de Negocio</label>
                <div className="form-value">
                  {getLabel(tiposCliente, cliente.businessType)}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Categoría Cliente</label>
                <div className="form-value">
                  {getLabel(categorias, cliente.clientCategory)}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Vendedor asignado</label>
                <div className="form-value">
                  {vendedor ? vendedor.full_name || vendedor.email :
                   cliente.assignedUserId ? `Vendedor #${cliente.assignedUserId}` : 'Sin asignar'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Menú de navegación inferior */}
      <BottomTabMenu
        baseRoute={`/system/clientes/${id}`}
        currentTab="datos"
      />

      {/* Modal de confirmación de borrado lógico */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Desactivar Cliente"
        message={`¿Desactivar el cliente "${cliente?.companyName || cliente?.fullName || ''}"? Seguirá en la base de datos como inactivo y podrá reactivarse luego.`}
        confirmText="Desactivar"
        cancelText="Cancelar"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default ClienteDetalle;
